import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useT } from "../i18n";

const LOGO_URL = "https://www.perfinco.pt/wp-content/uploads/2022/07/perfinco-logo.png";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/by-token/${token}`);
        setInvite(data);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || t("inv.invalid"));
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/register", {
        email: invite.email,
        password: form.password,
        name: form.name,
        invite_token: token,
      });
      navigate("/cliente");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen paper-grain flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-in">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <img src={LOGO_URL} alt="Perfinco" className="h-8" />
          <span className="h-6 w-px bg-[color:var(--border)]" />
          <span className="font-display text-sm font-medium tracking-[0.18em] uppercase text-[color:var(--ink-muted)]">{t("brand.suffix")}</span>
        </Link>
        <div className="card-soft" data-testid="invite-page">
          <span className="label-eyebrow">{t("inv.eyebrow")}</span>
          {error && (
            <div className="mt-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3" data-testid="invite-error">{error}</div>
          )}
          {!invite ? (
            !error && <p className="mt-4 text-[color:var(--ink-muted)]">{t("inv.validating")}</p>
          ) : (
            <>
              <h1 className="font-display text-2xl mt-2">{t("inv.welcome")} {invite.company_name}</h1>
              <p className="text-sm text-[color:var(--ink-muted)] mt-1">{t("inv.desc")}</p>
              <form onSubmit={onSubmit} className="mt-5 space-y-4">
                <div>
                  <label className="label-eyebrow block mb-2">{t("login.email")}</label>
                  <input className="input-soft bg-[color:var(--paper)]" value={invite.email} disabled />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">{t("inv.your_name")}</label>
                  <input className="input-soft" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="invite-name-input" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">{t("inv.password")}</label>
                  <input type="password" className="input-soft" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={6} data-testid="invite-password-input" />
                </div>
                <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="invite-submit-btn">
                  {submitting ? t("inv.creating") : t("inv.submit")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
